import React, { useEffect } from 'react';
import styled from 'styled-components';

declare global {
  interface Window {
    Calendly: any;
  }
}

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PopupContainer = styled.div`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  position: relative;
  
  @media (min-width: 768px) {
    width: 90%;
    height: 95%;
    max-width: 1000px;
    max-height: 850px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  cursor: pointer;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
`;

interface CalendlyPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onScheduled?: (event: any) => void;
  url?: string;
}

export const CalendlyPopup: React.FC<CalendlyPopupProps> = ({ 
  isOpen, 
  onClose,
  onScheduled,
  url = 'https://calendly.com/d/cxc7-wm7-wv7/meeting-phone-call-v2'
}) => {
  useEffect(() => {
    if (isOpen) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
      
      script.onload = () => {
        if (window.Calendly) {
          window.Calendly.initInlineWidget({
            url: url,
            parentElement: document.getElementById('calendly-inline-widget'),
            prefill: {},
            utm: {}
          });
        }
      };
      
      // Listen for Calendly event scheduled
      const handleMessage = (e: MessageEvent) => {
        if (e.data.event && e.data.event === 'calendly.event_scheduled') {
          onClose();
          if (onScheduled) {
            onScheduled(e.data);
          }
        }
      };
      
      window.addEventListener('message', handleMessage);
      document.body.style.overflow = 'hidden';
      
      return () => {
        window.removeEventListener('message', handleMessage);
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, url, onClose, onScheduled]);

  if (!isOpen) return null;

  return (
    <PopupOverlay onClick={onClose}>
      <PopupContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close">
          ✕
        </CloseButton>
        <div 
          id="calendly-inline-widget" 
          style={{ 
            width: '100%', 
            height: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            background: 'white'
          }}
        />
      </PopupContainer>
    </PopupOverlay>
  );
};